import React from 'react'

import { Stack, Typography, Box, DialogTitle, DialogContent, DialogActions } from '@mui/material'
import { WarningAmberOutlined, MoreTimeOutlined, PaidOutlined } from '@mui/icons-material'

import { numericUtils } from '@/utils/numeric'

import { ChargingStepProps, StartChargingStep } from '../hooks/useStartCharging/types'
import { PaymentSummary } from '../components/PaymentSummary'
import { ChargingStepActions } from '../components/ChargingStepActions'

type ConfirmationStepProps = ChargingStepProps

export const ConfirmationStep: React.FC<ConfirmationStepProps> = ({ startChargingMethods }) => {
  const {
    state,
    chargingPoint,
    selectedCard,
    pendingBalance,
    confirmPayment,
    goBack,
    goToStep,
    balance,
    hasIdleRate,
    isUsingWalletBalanceOnly,
  } = startChargingMethods
  const { amount, paymentMethod, isProcessing, useWalletBalance } = state
  const activationFee = chargingPoint?.current_activation_fee ?? chargingPoint?.activation_fee ?? 0

  const handleGoBack = () => {
    if (hasIdleRate) {
      goToStep(StartChargingStep.IDLE_RATE_CONFIRMATION)
    } else if (isUsingWalletBalanceOnly) {
      goToStep(StartChargingStep.AMOUNT)
    } else {
      goBack()
    }
  }

  return (
    <>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <WarningAmberOutlined color="warning" />
          <Typography variant="h6" fontWeight="bold" component="span">
            Confirmação
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3}>
          <PaymentSummary
            amount={amount ?? 0}
            ratePerKwh={chargingPoint?.current_rate_per_kwh ?? chargingPoint?.rate_per_kwh ?? 0}
            activationFee={activationFee}
            paymentMethod={paymentMethod}
            selectedCard={selectedCard}
            pendingBalance={pendingBalance}
            useWalletBalance={useWalletBalance}
            balance={balance}
          />

          <Typography variant="body2" color="text.secondary">
            <Typography component="span" fontWeight="bold" color="text.primary">
              Atenção:{' '}
            </Typography>
            Uma taxa adicional por ociosidade poderá ser cobrada caso o veículo permaneça conectado além do estimado.
          </Typography>

          <Box
            sx={{
              backgroundColor: 'action.hover',
              borderRadius: 2,
              padding: 2,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <MoreTimeOutlined color="primary" sx={{ fontSize: 28 }} />
              <Stack>
                <Typography variant="body2" color="text.secondary">
                  Taxa de ociosidade:
                </Typography>
                <Typography variant="subtitle1" fontWeight="bold" color="primary">
                  {numericUtils.formatNumberToBRLCurrency(chargingPoint?.current_idle_rate ?? chargingPoint?.idle_rate)}
                  /min
                </Typography>
              </Stack>
            </Stack>
          </Box>

          {activationFee > 0 && (
            <Box
              sx={{
                backgroundColor: 'action.hover',
                borderRadius: 2,
                padding: 2,
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <PaidOutlined color="primary" sx={{ fontSize: 28 }} />
                <Stack>
                  <Typography variant="body2" color="text.secondary">
                    Taxa de ativação:
                  </Typography>
                  <Typography variant="subtitle1" fontWeight="bold" color="primary">
                    {numericUtils.formatNumberToBRLCurrency(activationFee)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Cobrada só se a recarga iniciar no carregador.
                  </Typography>
                </Stack>
              </Stack>
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <ChargingStepActions sx={{ width: '100%' }}>
          <ChargingStepActions.ForwardButton onClick={confirmPayment} loading={isProcessing}>
            Confirmar
          </ChargingStepActions.ForwardButton>
          <ChargingStepActions.BackwardButton onClick={handleGoBack} disabled={isProcessing} />
        </ChargingStepActions>
      </DialogActions>
    </>
  )
}
